<template>
  <!--
    WP-03-CHAT-VISUAL-PARITY (doc 11) -- the prototype's `.chat-context`
    bar: an intent chip, a one-line summary, and the "more" button.

    D6 (human-closed): the "more" button is rendered exactly as in the
    prototype but is INERT in this package -- `aria-disabled`, out of the tab
    order, and it never opens anything. No menu behavior is invented.

    WP-04V-E (D6): the summary now reads like the prototype's --
    «ریاضی دهم · در یکی از مراحل گیر کردم» -- by prefixing the course/grade
    label. That label arrives as a PRESENTATION prop from the view, which gets
    it from SetupCard's separate `context-labels` event; the frozen
    `{intent, grade, subject, topic}` submit payload, `startSession` and every
    API payload are untouched. Without a label (e.g. after a page reload) the
    bar falls back to exactly what doc 11's deviation (c) allowed.
  -->
  <div class="ai-tutor-context" data-testid="ai-tutor-chat-context">
    <div class="ai-tutor-context__main">
      <span class="ai-tutor-context__chip" data-testid="ai-tutor-context-chip">
        <student-home-icon name="spark" :size="14" />
        {{ chipLabel }}
      </span>
      <strong
        class="ai-tutor-context__summary"
        data-testid="ai-tutor-context-summary"
        >{{ summary }}</strong
      >
    </div>
    <button
      type="button"
      class="ai-tutor-context__more"
      data-testid="ai-tutor-context-more"
      aria-disabled="true"
      tabindex="-1"
      :aria-label="$t('aiTutorSetup.chat.moreOptions')"
      @click.prevent
    >
      <student-home-icon name="more" :size="22" />
    </button>
  </div>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'

const KNOWN_INTENTS = ['troubleshoot', 'review', 'learn']

export default {
  name: 'ChatContextBar',
  components: { StudentHomeIcon },
  props: {
    intent: {
      type: String,
      default: '',
    },
    topic: {
      type: String,
      default: '',
    },
    // WP-04V-E (D6): presentation only, e.g. «ریاضی دهم».
    courseLabel: {
      type: String,
      default: '',
    },
    // The student's per-intent trailing choice, e.g. «در یکی از مراحل گیر
    // کردم». Presentation only -- it is local UI state in SetupCard and is
    // deliberately not in the submit payload.
    contextPhrase: {
      type: String,
      default: '',
    },
  },
  computed: {
    knownIntent() {
      return KNOWN_INTENTS.includes(this.intent) ? this.intent : ''
    },
    chipLabel() {
      return this.knownIntent
        ? this.$t(
            `aiTutorSetup.intentStep.options.${this.knownIntent}.pillLabel`
          )
        : this.$t('aiTutorSetup.eyebrow')
    },
    // The part after the course label. STUDENT-UX-02 / UX2-01 (doc 30 §6,
    // D-1): a typed topic is the conversation's subject and comes FIRST, the
    // per-intent choice after it («تابع درجه دوم · سطح آشنایی: …»); without a
    // topic the per-intent choice stands alone; with neither, the intent's
    // own title.
    summaryPhrase() {
      const specific = [this.topic, this.contextPhrase]
        .filter(Boolean)
        .join(' · ')
      if (specific) return specific
      return this.knownIntent
        ? this.$t(`aiTutorSetup.intentStep.options.${this.knownIntent}.title`)
        : this.$t('aiTutorSetup.chat.contextFallback')
    },
    summary() {
      const phrase = this.summaryPhrase
      // «ریاضی دهم · در یکی از مراحل گیر کردم», as the prototype composes it.
      return this.courseLabel ? `${this.courseLabel} · ${phrase}` : phrase
    },
  },
}
</script>

<style lang="scss" scoped>
// Measured from the rendered canonical prototype (`.chat-context`, sha256
// 75678bee...; 1920x1040 and 390x844).
$ai-tutor-border: $borna-border;
$ai-tutor-canvas: $borna-canvas;
$ai-tutor-indigo-50: $borna-indigo-50;
$ai-tutor-indigo-700: $borna-indigo-900;

.ai-tutor-context {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
  padding: 12px 15px;
  border: 1px solid $ai-tutor-border;
  border-radius: 16px;
  background: #fff;
}

.ai-tutor-context__main {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.ai-tutor-context__chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  padding: 6px 9px;
  border-radius: 999px;
  background: $ai-tutor-indigo-50;
  color: $ai-tutor-indigo-700;
  font-size: 10px;
  font-weight: 800;
  white-space: nowrap;
}

.ai-tutor-context__summary {
  font-size: 12px;
  font-weight: 700;
}

.ai-tutor-context__more {
  display: grid;
  place-items: center;
  flex: none;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: $ai-tutor-canvas;
  color: inherit;
  cursor: default;
}

// The prototype's <=620px rule: chip stacks above the summary.
@media (max-width: 620px) {
  .ai-tutor-context__main {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
}
</style>
